import { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import { useAuth } from "@/context/AuthContext";
import Logger from "@/utility/logger";
import type { AgentTaskSummary, GroundingArtifact } from "@/types/grounding";

interface RetrieveDocumentsParams {
  projectId: string;
  tagIds?: string[];
  state?: string;
  filename?: string;
  tagFilterMode?: "AND" | "OR";
  sortBy?: string;
  page?: number;
  perPage?: number;
}

interface RetrieveDocumentsForImageInsertionParams {
  projectId: string;
  filename?: string;
  state?: string;
  sortBy?: string;
  page?: number;
  perPage?: number;
}

interface DownloadDocumentImageRequest {
  documentId: string;
  page: number;
  size: string;
  dpi: number;
  format: string;
  color: string;
}

interface RetrieveTagsParams {
  report_template_id?: string;
  project_id?: string;
  visible?: boolean;
  search?: string;
  per_page?: number;
}

interface ChatAsyncStreamEvent {
  status?: string;
  task_id?: string;
  message?: string;
  chat_task_status?: string;
  event_seq?: number;
  updated_at?: string;
  chat_history_id?: string;
  chat_history?: any;
  error_message?: string;
  error?: string;
}

type ChatAsyncProgressCallback = (event: ChatAsyncStreamEvent) => void;

interface ChatAsyncTaskResponse {
  id: string;
  status: string;
  chat_history_id?: string | null;
  error_message?: string | null;
  project_id: string;
}

const useBackendAPIs = (_baseURL: string, _jwtToken: string, searchType: string) => {
  const { axios: client } = useAuth();

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize);
      const charCodes = new Array(chunk.length);

      for (let offset = 0; offset < chunk.length; offset++) {
        charCodes[offset] = chunk[offset];
      }

      binary += String.fromCharCode.apply(null, charCodes);
    }

    if (typeof globalThis.btoa === "function") {
      return globalThis.btoa(binary);
    }

    throw new Error("Base64 encoding is not available in this environment");
  };

  // Generic helper to fetch all pages for endpoints that return { items, pages }
  const fetchAllPages = async <T,>(
    basePath: string,
    baseParams: Record<string, any> = {},
    itemsSelector: (data: any) => T[] = (data) => data.items || []
  ): Promise<T[]> => {
    let page = 1;
    const all: T[] = [];

    while (true) {
      const params = { ...baseParams, page };
      const res = await client.get(basePath, { params });
      if (res.status !== 200 || !res.data) {
        throw new Error(res.data?.error || "Failed to fetch data");
      }
      const items = itemsSelector(res.data);
      all.push(...items);
      const totalPages = res.data.pages;
      if (page >= totalPages) break;
      page += 1;
    }
    return all;
  };

  // Retrieves the status of the API key by making a request to the projects endpoint.
  const retrieveKeyStatus = async () => {
    try {
      let axiosConfig: AxiosRequestConfig = {};
      const url = `/projects`;
      const res = await client.get(url, axiosConfig);

      return true;
    } catch (error) {
      return false;
    }
  };

  // Retrieves all projects from the backend API
  const retrieveProjects = async () => {
    try {
      const items = await fetchAllPages<any>(`/projects`, { per_page: 100 });
      return items;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: retrieveProjects error:", error);
      throw error;
    }
  };

  // Retrieves all settings groups for a specific report template ID
  const retrieveSettingsGroup = async (report_template_id: string) => {
    const items = await fetchAllPages<any>(`/settings-groups`, { report_template_id, is_enabled: true, per_page: 100 });
    return items;
  };

  // Retrieves all tags for a report template or project
  const retrieveTags = async ({
    report_template_id,
    project_id,
    visible,
    search,
    per_page = 1000,
  }: RetrieveTagsParams = {}) => {
    const items = await fetchAllPages<any>(`/tags`, {
      report_template_id,
      project_id,
      visible,
      search,
      per_page,
    });

    return items;
  };

  // Helper for POST /chat calls
  const chatRequest = async <T = any,>(payload: any) => {
    let axiosConfig: AxiosRequestConfig = {
      timeout: 5 * 60 * 1000, // 5 minutes timeout for chat requests, default is 60seconds on Mac.
    };
    const url = `/chat`;
    const res = await client.post(url, payload, axiosConfig);

    Logger.debug("useBackendAPIs.tsx: Received response from /chat API");

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    return (res.data as T) ?? ([] as unknown as T);
  };

  const retrieveChatHistory = async (chat_history_id: string) => {
    const res = await client.get(`/chat/${chat_history_id}`);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch chat history");
    }

    return res.data || {};
  };

  const submitChatAsyncRequest = async (payload: any) => {
    const res = await client.post(`/chat-async`, payload, {
      timeout: 60 * 1000,
    });

    Logger.debug("useBackendAPIs.tsx: Submitted request to /chat-async API");

    if (res.status !== 202 || !res.data?.task_id) {
      throw new Error(res.data?.error || "Failed to submit async chat request");
    }

    return res.data;
  };

  const retrieveChatAsyncTask = async (task_id: string): Promise<ChatAsyncTaskResponse> => {
    const res = await client.get(`/chat-async/${task_id}`);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch async chat task status");
    }

    return res.data;
  };

  const streamSSE = (
    url: string,
    onMessage: (data: any) => void,
    onError: (error: Error) => void,
    onClose: (intentional?: boolean) => void,
    options: { timeout?: number; streamName?: string } = {}
  ) => {
    const controller = new AbortController();
    let lastResponseLength = 0;
    let buffer = "";
    const streamName = options.streamName || "SSE";

    client
      .get(url, {
        signal: controller.signal,
        responseType: "text",
        timeout: options.timeout ?? 0,
        headers: {
          Accept: "text/event-stream",
        },
        onDownloadProgress: (progressEvent) => {
          try {
            const xhr = progressEvent.event?.target as XMLHttpRequest;
            if (!xhr?.responseText) return;

            const newText = xhr.responseText.substring(lastResponseLength).replace(/\r/g, "");
            lastResponseLength = xhr.responseText.length;
            buffer += newText;

            let eolIndex;
            while ((eolIndex = buffer.indexOf("\n\n")) !== -1) {
              const messageBlock = buffer.substring(0, eolIndex);
              buffer = buffer.substring(eolIndex + 2);

              const lines = messageBlock.split("\n");
              for (const line of lines) {
                const startToken = "data:";
                if (line.startsWith(startToken)) {
                  const dataString = line.substring(startToken.length).trimStart();
                  try {
                    const parsedData = JSON.parse(dataString);
                    onMessage(parsedData);
                  } catch (error: any) {
                    Logger.warn(`useBackendAPIs.tsx: Failed to parse ${streamName} JSON:`, error.message);
                  }
                }
              }
            }
          } catch (error: any) {
            Logger.error(`useBackendAPIs.tsx: Failed to parse ${streamName} message:`, error.message);
            onError(new Error(`Failed to parse ${streamName} message: ${error.message}`));
          }
        },
      })
      .then(() => {
        onClose(false);
      })
      .catch((error) => {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          Logger.debug(`useBackendAPIs.tsx: ${streamName} stream aborted by client.`);
          onClose(true);
        } else {
          onError(error);
          onClose(false);
        }
      });

    return controller;
  };

  const streamChatAsyncTask = (
    task_id: string,
    onMessage: (data: ChatAsyncStreamEvent) => void,
    onError: (error: Error) => void,
    onClose: (intentional?: boolean) => void
  ) => streamSSE(`/chat-async/${task_id}/stream`, onMessage, onError, onClose, { streamName: "chat-async SSE" });

  const waitForChatAsyncResult = async (task_id: string, onProgress?: ChatAsyncProgressCallback) => {
    return await new Promise<any>((resolve, reject) => {
      let controller: AbortController | null = null;
      let settled = false;
      let statusCheckStarted = false;

      const cleanup = () => {
        if (controller) {
          controller.abort();
          controller = null;
        }
      };

      const completeWithEvent = async (data: ChatAsyncStreamEvent) => {
        if (data.chat_history) {
          return data.chat_history;
        }

        if (data.chat_history_id) {
          return await retrieveChatHistory(data.chat_history_id);
        }

        throw new Error("Async chat task completed without chat history");
      };

      const settleResolve = (value: any) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(value);
      };

      const settleReject = (error: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(error);
      };

      const getResultFromTaskStatus = async (reason: string) => {
        const task = await retrieveChatAsyncTask(task_id);
        const status = task.status?.toLowerCase();

        if (status === "completed") {
          if (!task.chat_history_id) {
            throw new Error(`Async chat task ${task_id} completed without chat history id`);
          }
          return await retrieveChatHistory(task.chat_history_id);
        }

        if (status === "failed") {
          const errorMessage = task.error_message || "Async chat task failed";
          throw new Error(`Async chat task ${task_id} failed: ${errorMessage}`);
        }

        throw new Error(`Async chat task ${task_id} did not complete after ${reason}; latest status: ${task.status}`);
      };

      const finishFromTaskStatus = async (reason: string) => {
        if (statusCheckStarted || settled) {
          return;
        }
        statusCheckStarted = true;

        try {
          const result = await getResultFromTaskStatus(reason);
          settleResolve(result);
        } catch (error: any) {
          settleReject(error);
        }
      };

      const finishFromCompletedEvent = async (data: ChatAsyncStreamEvent) => {
        try {
          const result = await completeWithEvent(data);
          settleResolve(result);
        } catch (error: any) {
          settleReject(error);
        }
      };

      controller = streamChatAsyncTask(
        task_id,
        (data) => {
          if (settled) return;

          onProgress?.(data);

          const status = data.status?.toLowerCase();
          if (status === "completed") {
            finishFromCompletedEvent(data);
          } else if (status === "failed") {
            Logger.warn("useBackendAPIs.tsx: Chat async stream reported failure. Resolving via task status:", data);
            finishFromTaskStatus("stream failure");
          } else if (status === "timeout") {
            settleReject(new Error(`Async chat task ${task_id} timed out`));
          } else if (status === "error") {
            Logger.warn("useBackendAPIs.tsx: Chat async stream reported error. Resolving via task status:", data);
            finishFromTaskStatus("stream error");
          }
        },
        (error) => {
          Logger.warn("useBackendAPIs.tsx: Chat async stream error. Resolving via task status:", error);
          finishFromTaskStatus("stream error");
        },
        (intentional) => {
          if (settled || intentional) return;
          Logger.warn("useBackendAPIs.tsx: Chat async stream closed before completion. Resolving via task status.");
          finishFromTaskStatus("stream close");
        }
      );
    });
  };

  const chatAsyncRequest = async <T = any,>(payload: any, onProgress?: ChatAsyncProgressCallback) => {
    const task = await submitChatAsyncRequest(payload);
    onProgress?.({
      status: "running",
      task_id: task.task_id,
      message: task.message || "Async chat task enqueued",
      chat_task_status: "ENQUEUED",
    });
    const chatHistory = await waitForChatAsyncResult(task.task_id, onProgress);

    Logger.debug("useBackendAPIs.tsx: Received completed response from /chat-async API");

    return (chatHistory as T) ?? ([] as unknown as T);
  };

  // Retrieves the LLM output for a specific project and settings group
  const retrieveLLMOuput = async (project_id: string, settings_group_id: string) => {
    const data = {
      project_id,
      search_type: searchType,
      settings_group_id,
    };

    return await chatRequest(data);
  };

  const retrieveLLMOuputAsync = async (
    project_id: string,
    settings_group_id: string,
    onProgress?: ChatAsyncProgressCallback
  ) => {
    const data = {
      project_id,
      search_type: searchType,
      settings_group_id,
      // Section generation produces report-section rows, which must NOT
      // appear on the chat with documents page. Stamp the row as
      // non-interactive explicitly (defensive — the backend default is the
      // same, but explicit declarations keep the data model clear).
      source: "non_interactive_chat" as const,
    };

    return await chatAsyncRequest(data, onProgress);
  };

  // Retrieves all report templates for a specific project
  const retrieveReportTemplates = async (project_id: string) => {
    const items = await fetchAllPages<any>(`/report-templates`, { project_id, per_page: 100 });
    return items;
  };

  // Retrieves the base 64 string for a specific report template
  const retrieveBase64 = async (template_info: any) => {
    let axiosConfig: AxiosRequestConfig = {};

    const url = `/report-templates/${template_info.id}/download`;

    const res = await client.get(url, axiosConfig);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }

    const template = {
      ...template_info,
      ...res.data,
    };

    return template;
  };

  // Retrieves the placeholder's list for a specific report template
  const retrievePlaceholders = async (template_id: any) => {
    try {
      let axiosConfig: AxiosRequestConfig = {};

      const url = `/report-templates/${template_id}/placeholders`;

      const res = await client.get(url, axiosConfig);

      if (res.status !== 200 || !res.data) {
        throw new Error(res.data?.error || `Failed to fetch placeholder data for template ${template_id}`);
      }

      const template = {
        ...template_id,
        ...res.data,
      };

      return template;
    } catch (error) {
      Logger.error(`useBackendAPIs.tsx: Error fetching placeholders for template`, error);
    }
  };

  // Retrieves the evaluation results for a specific chat
  const retrieveChatEvaluation = async (chat_id: string) => {
    let axiosConfig: AxiosRequestConfig = {};

    const url = `/chat/${chat_id}/evaluate`;

    const res = await client.post(url, {}, axiosConfig);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const items = res.data || [];

    return items;
  };

  // Retrieves the comparison results for a specific chat
  const retrieveChatComparison = async (chat_id: string) => {
    let axiosConfig: AxiosRequestConfig = {
      params: {
        comparison_llm_name: "gpt-6-luna",
      },
    };

    const url = `/chat/compare/${chat_id}`;

    const res = await client.get(url, axiosConfig);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const items = res.data || [];

    return items;
  };

  // Submits feedback for a specific chat
  const submitFeedback = async (chat_id: string, feedback: string, generate_response: Boolean) => {
    let axiosConfig: AxiosRequestConfig = {};

    const data = {
      feedback,
      generate_response,
    };

    const url = `/chat/${chat_id}/feedback`;

    const res = await client.post(url, data, axiosConfig);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const items = res.data || [];

    return items;
  };

  const retrieveSettingsGroupByName = async (settings_group_name: string) => {
    try {
      const url = `/settings-groups/by-name/${settings_group_name}`;

      const res = await client.get(url);

      if (res.status !== 200 || !res.data) {
        throw new Error(res.data?.error || "Failed to fetch settings group");
      }
      return res.data;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      Logger.error(`useBackendAPIs.tsx: Error fetching settings group '${settings_group_name}':`, error);
      throw error;
    }
  };

  // Model & Tag Retrieval APIs
  const getModels = async (agentic: boolean = false) => {
    const res = await client.get("/models", {
      params: { agentic: agentic ? "true" : "false" },
    });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve models");
    }
    return res.data;
  };

  // Tags for the chat filter: only those attached to at least one document of
  // the project, since any other tag could never match in the chat's scope.
  const getProjectTags = async (projectId: string, perPage: number = 1000) => {
    const params = new URLSearchParams();
    params.append("project_id", projectId);
    params.append("used_in_project", "true");
    params.append("per_page", String(perPage));

    const res = await client.get("/tags", { params });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve tags");
    }
    return res.data;
  };

  const getPromptTemplates = async (perPage: number = 100) => {
    const res = await client.get("/prompt-templates", {
      params: { per_page: perPage },
    });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve prompt templates");
    }
    return res.data?.items || res.data || [];
  };

  const buildWOGroupChatPayload = async (
    project_id: string,
    previous_chat_history_id: string | null,
    question: string,
    uploaded_file_tag_ids: string[] | null,
    section_name?: string | null,
    selectedReportTemplateId?: string | null,
    source: "interactive_chat" | "non_interactive_chat" = "interactive_chat",
    options?: {
      llm_name?: string;
      include_tag_ids?: string[];
      exclude_tag_ids?: string[];
    }
  ) => {
    let fetched_settings_group_data = null;
    const isGenericChat = !section_name || section_name === "chat";

    try {
      if (section_name) {
        fetched_settings_group_data = await retrieveSettingsGroupByName(section_name);
      } else {
        fetched_settings_group_data = await retrieveSettingsGroupByName("chat");
      }
    } catch (err) {
      if (!isGenericChat) throw err;
      fetched_settings_group_data = null;
    }

    if (!fetched_settings_group_data && !isGenericChat) {
      throw new Error(`Failed to fetch settings group '${section_name}'`);
    }

    const settings_group_data = fetched_settings_group_data;
    const settings = settings_group_data?.settings || {};
    let prompt_template_id =
      settings_group_data?.system_prompt_id && settings_group_data.system_prompt_id !== "default"
        ? settings_group_data.system_prompt_id
        : undefined;

    if (!prompt_template_id && isGenericChat) {
      try {
        const templates = await getPromptTemplates(100);
        const list = Array.isArray(templates) ? templates : templates?.items || [];
        const defaultTemplate =
          list.find((t: any) => t.is_default || t.name?.toLowerCase().includes("default") || t.name?.toLowerCase().includes("chat")) ||
          list[0];
        if (defaultTemplate?.id) {
          prompt_template_id = defaultTemplate.id;
        }
      } catch (err) {
        Logger.warn("useBackendAPIs: Could not resolve fallback prompt template for chat:", err);
      }
    }

    let llm_name = options?.llm_name || settings?.model;
    if (!llm_name && isGenericChat) {
      try {
        const modelList = await getModels(false);
        const list = Array.isArray(modelList) ? modelList : modelList?.items || [];
        if (list.length > 0) {
          llm_name = list[0]?.id || list[0]?.name;
        }
      } catch (err) {
        Logger.warn("useBackendAPIs: Could not resolve fallback model for chat:", err);
      }
    }

    let include_tag_ids = new Set<string>();

    if (uploaded_file_tag_ids?.length) {
      for (const tagId of uploaded_file_tag_ids) {
        if (tagId) include_tag_ids.add(tagId);
      }
    }

    if (options?.include_tag_ids?.length) {
      for (const tagId of options.include_tag_ids) {
        if (tagId) include_tag_ids.add(tagId);
      }
    } else if (settings_group_data?.include_tag_ids?.length) {
      for (const tagId of settings_group_data.include_tag_ids) {
        if (tagId) include_tag_ids.add(tagId);
      }
    }

    const rawTemplateId = selectedReportTemplateId || settings_group_data?.report_template_id;
    const report_template_id = rawTemplateId && rawTemplateId !== "default" ? rawTemplateId : undefined;
    const exclude_tag_ids = options?.exclude_tag_ids ?? settings_group_data?.exclude_tag_ids;

    const validSource: "interactive_chat" | "non_interactive_chat" =
      source === "non_interactive_chat" ? "non_interactive_chat" : "interactive_chat";

    const temperature =
      typeof settings?.temperature === "number" ? settings.temperature : 0.0;
    const num_of_sources =
      typeof settings?.num_of_sources === "number" ? settings.num_of_sources : 30;
    const score_threshold =
      typeof settings?.score_threshold === "number" ? settings.score_threshold : 0.5;
    const run_quality_checks =
      typeof settings_group_data?.run_quality_checks === "boolean"
        ? settings_group_data.run_quality_checks
        : false;

    return {
      project_id: project_id,
      previous_chat_history_id: previous_chat_history_id,
      question: question,
      llm_name: llm_name,
      prompt_template_id,
      report_template_id,
      temperature,
      num_of_sources,
      score_threshold,
      search_type: searchType || "hybrid",
      include_tag_ids: include_tag_ids.size > 0 ? Array.from(include_tag_ids) : undefined,
      exclude_tag_ids: exclude_tag_ids?.length ? exclude_tag_ids : undefined,
      run_quality_checks,
      source: validSource,
    };
  };

  /**
   * Used for Q&A chat through the synchronous /chat API.
   *
   * `source` defaults to "interactive_chat" because the add-in's chat
   * page is one of the two surfaces that opt in (the other being the
   * workspace's chat with documents page). Callers that produce
   * non-interactive rows (e.g. report section generation) MUST pass
   * "non_interactive_chat" explicitly.
   */
  const retrievellmOuputWOGroup = async (
    project_id: string,
    previous_chat_history_id: string | null,
    question: string,
    uploaded_file_tag_ids: string[] | null,
    section_name?: string | null,
    selectedReportTemplateId?: string | null,
    sourceOrOptions?:
      | "interactive_chat"
      | "non_interactive_chat"
      | {
          llm_name?: string;
          include_tag_ids?: string[];
          exclude_tag_ids?: string[];
        }
      | string[],
    maybeExcludeTagIdsOrSource?: string[] | "interactive_chat" | "non_interactive_chat",
    maybeLlmName?: string,
    maybeSource?: "interactive_chat" | "non_interactive_chat"
  ) => {
    let source: "interactive_chat" | "non_interactive_chat" = "interactive_chat";
    let options:
      | {
          llm_name?: string;
          include_tag_ids?: string[];
          exclude_tag_ids?: string[];
        }
      | undefined = undefined;

    if (sourceOrOptions === "non_interactive_chat" || sourceOrOptions === "interactive_chat") {
      source = sourceOrOptions;
      if (typeof maybeExcludeTagIdsOrSource === "object" && !Array.isArray(maybeExcludeTagIdsOrSource)) {
        options = maybeExcludeTagIdsOrSource;
      }
    } else if (sourceOrOptions && typeof sourceOrOptions === "object" && !Array.isArray(sourceOrOptions)) {
      options = sourceOrOptions;
      if (maybeExcludeTagIdsOrSource === "non_interactive_chat" || maybeExcludeTagIdsOrSource === "interactive_chat") {
        source = maybeExcludeTagIdsOrSource;
      }
    } else if (Array.isArray(sourceOrOptions) || Array.isArray(maybeExcludeTagIdsOrSource) || maybeLlmName || maybeSource) {
      options = {
        include_tag_ids: Array.isArray(sourceOrOptions) ? sourceOrOptions : undefined,
        exclude_tag_ids: Array.isArray(maybeExcludeTagIdsOrSource) ? maybeExcludeTagIdsOrSource : undefined,
        llm_name: maybeLlmName,
      };
      if (maybeSource === "non_interactive_chat" || maybeSource === "interactive_chat") {
        source = maybeSource;
      }
    }

    const data = await buildWOGroupChatPayload(
      project_id,
      previous_chat_history_id,
      question,
      uploaded_file_tag_ids,
      section_name,
      selectedReportTemplateId,
      source,
      options
    );

    return await chatRequest(data);
  };

  const retrievellmOuputWOGroupAsync = async (
    project_id: string,
    previous_chat_history_id: string | null,
    question: string,
    uploaded_file_tag_ids: string[] | null,
    section_name?: string | null,
    selectedReportTemplateId?: string | null,
    onProgress?: ChatAsyncProgressCallback,
    source: "interactive_chat" | "non_interactive_chat" = "interactive_chat"
  ) => {
    const data = await buildWOGroupChatPayload(
      project_id,
      previous_chat_history_id,
      question,
      uploaded_file_tag_ids,
      section_name,
      selectedReportTemplateId,
      source
    );

    return await chatAsyncRequest(data, onProgress);
  };

  // Current assumption: one project will not exceed 100 documents.
  const retrieveDocumentsList = async ({ projectId, tagIds, page, perPage }: RetrieveDocumentsParams) => {
    const url = "/documents";

    const params = new URLSearchParams();
    params.append("project_id", projectId);
    params.append("sort_by", "filename");

    if (typeof page === "number") {
      params.append("page", page.toString());
    }

    if (typeof perPage === "number") {
      params.append("per_page", perPage.toString());
    }

    if (tagIds?.length) {
      tagIds.forEach((tagId) => {
        params.append("tag_ids", tagId);
      });
    }

    const res = await client.get(url, { params });

    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve documents list");
    }

    return res.data;
  };

  const retrieveDocumentsForImageInsertion = async ({
    projectId,
    filename,
    state,
    sortBy = "filename",
    page = 1,
    perPage = 100,
  }: RetrieveDocumentsForImageInsertionParams) => {
    const url = "/documents";

    const params = new URLSearchParams();
    params.append("project_id", projectId);
    params.append("sort_by", sortBy);
    params.append("page", page.toString());
    params.append("per_page", perPage.toString());

    if (filename) {
      params.append("filename", filename);
    }

    if (state) {
      params.append("state", state);
    }

    const res = await client.get(url, { params });

    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve documents for image insertion");
    }

    return res.data;
  };

  const retrieveDocumentImages = async (documentId: string) => {
    const url = `/documents/${documentId}/images`;

    const res = await client.get(url);

    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve document images");
    }

    return res.data;
  };

  const downloadDocumentImage = async ({
    documentId,
    page,
    size,
    dpi,
    format,
    color,
  }: DownloadDocumentImageRequest) => {
    const url = `/documents/${documentId}/download-image`;

    const res = await client.get(url, {
      params: {
        page,
        size,
        dpi,
        format,
        color,
      },
      responseType: "arraybuffer",
      headers: {
        Accept: "*/*",
      },
    });

    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to download document image");
    }

    return arrayBufferToBase64(res.data);
  };

  // Retrieves the source document for a specific document ID
  const retrieveSourceDocument = async (document_id: string) => {
    let axiosConfig = {};

    const url = `/documents/${document_id}/download`;

    const res = await client.get(url, axiosConfig as any);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const items = res.data || [];

    return items;
  };

  // Uploads a document to a specific project with associated tags
  // tag_ids = []
  // create_temp_tag = true
  const uploadDocument = async (project_id: string, tag_ids: string[], fileBinary: File) => {
    let axiosConfig = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    const formData = new FormData();

    formData.append("file", fileBinary);
    formData.append("project_id", project_id);
    formData.append("create_temp_tag", "true");

    if (tag_ids) {
      formData.append("tag_ids", JSON.stringify(tag_ids));
    }

    const url = `/documents`;

    try {
      const res = await client.post(url, formData, axiosConfig);

      if (res.status !== 201 || !res.data) {
        throw new Error(res.data?.error || "Failed to fetch data");
      }
      const document = res.data || {};

      Logger.debug("useBackendAPIs.tsx: Document uploaded successfully", document?.id);

      return document;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error uploading document", error);
      throw error;
    }
  };

  const processDocument = async (
    documentId: string,
    shouldTriggerTagSuggestions: boolean = true,
    skip_vectorization: boolean = true
  ) => {
    let axiosConfig: AxiosRequestConfig = {
      params: {
        should_trigger_tag_suggestions: shouldTriggerTagSuggestions,
        skip_vectorization: skip_vectorization,
      },
    };

    const url = `/documents/${documentId}/process`;

    const res = await client.post(url, null, axiosConfig);

    if (res.status !== 202 || !res.data) {
      throw new Error(res.data?.detail || "Failed to process document");
    }

    return res.data;
  };

  const retreiveSourceDocumentMetadata = async (document_id: string) => {
    let axiosConfig = {};

    const url = `/documents/${document_id}`;

    try {
      const res = await client.get(url, axiosConfig as any);

      if (res.status !== 200) {
        throw new Error(res.data?.error || "Failed to fetch data");
      }

      const message = res.data || {};

      return message;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error fetching document metadata", error);
    }
  };

  // Deletes a document by its ID
  const deleteDocument = async (document_id: string) => {
    let axiosConfig = {};

    const url = `/documents/${document_id}`;

    const res = await client.delete(url, axiosConfig as any);

    if (res.status !== 204) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const message = res.data || {};

    return message;
  };

  // Creates a new tag with the specified name and description
  const createTag = async (name: string, description: string) => {
    let axiosConfig = {};

    const data = {
      name,
      description,
      color: "#808080",
      visible: false,
    };

    const url = `/tags`;

    const res = await client.post(url, data, axiosConfig as any);

    if (res.status !== 201 || !res.data) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const tag = res.data || {};

    return tag;
  };

  // Deletes a tag by its ID
  const deleteTag = async (tag_id: string) => {
    let axiosConfig = {
      params: {
        hard_delete: true,
      },
    };

    const url = `/tags/${tag_id}`;

    const res = await client.delete(url, axiosConfig as any);

    if (res.status !== 204) {
      throw new Error(res.data?.error || "Failed to fetch data");
    }
    const message = res.data || {};

    return message;
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    let axiosConfig = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    const wavBlob =
      audioBlob.type === "audio/wav"
        ? audioBlob
        : new Blob([audioBlob], { type: "audio/wav" });

    const formData = new FormData();
    formData.append("audio", wavBlob, "chat_audio.wav");
    formData.append("locale", "en-US");

    const url = "/speech/transcribe";

    const res = await client.post(url, formData, axiosConfig as any);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to transcribe audio file");
    }

    const message = res.data?.text?.trim() || "";
    Logger.debug("useBackendAPIs.tsx: Transcription result length:", message.length);

    return message;
  };

  /** POST /speech/synthesize — neural TTS; returns the spoken audio as a Blob. */
  const synthesizeSpeech = async (text: string, voice: string = "en-US-AvaMultilingualNeural"): Promise<Blob> => {
    const res = await client.post(
      "/speech/synthesize",
      { text, voice },
      {
        responseType: "blob",
        headers: { "Content-Type": "application/json" },
      }
    );

    if (res.status !== 200 || !res.data) {
      throw new Error((res.data as any)?.error || "Failed to synthesize speech");
    }

    return res.data;
  };

  /** Speech endpoints grouped like the workspace's speechApi. */
  const speechApi = useMemo(
    () => ({
      transcribe: transcribeAudio,
      synthesize: synthesizeSpeech,
    }),
    // transcribeAudio/synthesizeSpeech close over only `client`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client]
  );

  const retrieveAgentQuery = async (project_id: string, settings_group_id: string, snapshot_document_id?: string) => {
    const data = {
      project_id,
      settings_group_id,
      ...(snapshot_document_id ? { snapshot_document_id } : {}),
    };

    try {
      const res = await client.post(`/agent/query`, data);

      if (res.status !== 202 || !res.data) {
        throw new Error(res.data?.error || "Failed to submit agent query");
      }

      return res.data;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error running agent query:", error);
      throw error;
    }
  };

  const retrieveAgentStatus = async (task_id: string) => {
    let axiosConfig = {};

    try {
      const res = await client.get(`/agent/status/${task_id}`, axiosConfig as any);

      if (res.status !== 200 || !res.data) {
        throw new Error(res.data?.error || "Failed to submit agent status");
      }

      return res.data;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error fetching agent status:", error);
      throw error;
    }
  };

  const retrieveAgentTasks = async (
    project_id: string,
    options: { page?: number; per_page?: number } = {}
  ): Promise<AgentTaskSummary[]> => {
    try {
      const res = await client.get<{ items: AgentTaskSummary[] }>("/agent/tasks", {
        params: {
          project_id,
          page: options.page || 1,
          per_page: options.per_page || 100,
        },
      });

      if (res.status !== 200 || !res.data) {
        throw new Error((res.data as any)?.error || "Failed to retrieve agent tasks");
      }

      return res.data.items || [];
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error fetching agent task history:", error);
      throw error;
    }
  };

  const retrieveAgentGrounding = async (task_id: string): Promise<GroundingArtifact> => {
    try {
      const res = await client.get<GroundingArtifact>(`/agent/tasks/${task_id}/grounding`);

      if (res.status !== 200 || !res.data) {
        throw new Error((res.data as any)?.error || "Failed to retrieve agent grounding");
      }

      return res.data;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error fetching agent grounding:", error);
      throw error;
    }
  };

  const streamGetRequest = (
    task_id: string,
    onMessage: (data: any) => void,
    onError: (error: Error) => void,
    onClose: () => void
  ) => {
    let isCompleted = false;
    let isFailed = false;
    return streamSSE(
      `/agent/status/${task_id}/stream`,
      (data) => {
        if (data?.status?.toLowerCase() === "completed") {
          isCompleted = true;
        } else if (data?.status?.toLowerCase() === "failed") {
          data.response_data = {};
          data.response_data.answer = `ERROR: AI was not able generate content for this section`;
          isFailed = true;
        } else if (data?.event?.toLowerCase() === "stream_end") {
          if (isCompleted === false && isFailed === false) {
            data.response_data = {};
            data.response_data.answer = `TODO: AI didn't generated content successfully`;
          }
          return;
        }

        onMessage(data);
      },
      (error) => {
        onError(error);
      },
      () => {
        onClose();
      },
      { timeout: 15 * 60 * 1000, streamName: "agent SSE" }
    );
  };

  const logAuditAction = async (
    report_template_project_id: string,
    action: "CREATE" | "UPDATE" | "DELETE",
    section_name?: string | null,
    reason?: string | null
  ) => {
    try {
      const session_id = localStorage.getItem("Office App Log Id Set");
      const url = `/audit-trail/${report_template_project_id}/actions`;
      const data = {
        action,
        source_system: "jenly-addin",
        section_name,
        session_id,
        reason,
      };

      const res = await client.post(url, data);
      if (res.status !== 201) {
        Logger.warn("useBackendAPIs.tsx: Audit trail logging returned non-201 status:", res.status);
      }
      return res.data;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: Error logging audit action:", error);
      // We don't throw here to avoid breaking the main flow if audit logging fails
    }
  };

  const linkProjectToTemplate = async (project_id: string, template_id: string, placeholder_values: any = {}) => {
    try {
      const url = `/projects/${project_id}/templates/${template_id}`;
      const data = {
        placeholder_values,
      };

      const res = await client.post(url, data);

      if (res.status !== 200) {
        throw new Error(res.data?.detail || "Failed to link project to template");
      }

      return res.data;
    } catch (error) {
      Logger.error("useBackendAPIs.tsx: linkProjectToTemplate error:", error);
      throw error;
    }
  };

  const submitLangfuseFeedback = async (chat_history_id: string, rating: number, comment?: string) => {
    if (!chat_history_id) {
      Logger.error("useBackendAPIs.tsx: submitLangfuseFeedback called without chat_history_id");
      return;
    }
    const url = `/chat/${chat_history_id}/langfuse-feedback`;

    const data = {
      rating,
      comment,
    };

    const res = await client.post(url, data);

    if (res.status !== 200 || !res.data) {
      throw new Error(res.data?.error || "Failed to submit Langfuse feedback");
    }

    // res = { success, score_id, trace_id, message }
    return res.data;
  };

  // ────────────────────────────────────────────
  // Chat History Management APIs
  // ────────────────────────────────────────────

  /** GET /chat/project/{project_id} — paginated chat history for a project */
  const getProjectChatHistory = async (
    projectId: string,
    options?: {
      page?: number;
      per_page?: number;
      pinned_only?: boolean;
      conversations_only?: boolean;
    }
  ) => {
    const params = new URLSearchParams();
    params.append("page", String(options?.page ?? 0));
    params.append("per_page", String(options?.per_page ?? 30));
    if (options?.pinned_only) params.append("pinned_only", "true");
    if (options?.conversations_only) params.append("conversations_only", "true");

    const res = await client.get(`/chat/project/${projectId}`, { params });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve chat history");
    }
    return res.data;
  };

  /** GET /chat/{chat_history_id}/thread — full conversation thread */
  const getChatThread = async (chatHistoryId: string) => {
    const res = await client.get(`/chat/${chatHistoryId}/thread`);
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to retrieve chat thread");
    }
    return res.data;
  };

  /** PATCH /chat/{chat_history_id}/pin — pin or unpin a chat */
  const setChatPinned = async (chatHistoryId: string, isPinned: boolean) => {
    const res = await client.patch(`/chat/${chatHistoryId}/pin`, {
      is_pinned: isPinned,
    });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to update pin state");
    }
    return res.data;
  };

  /** PATCH /chat/{chat_history_id}/rename — rename a chat */
  const renameChatHistory = async (chatHistoryId: string, title: string) => {
    const res = await client.patch(`/chat/${chatHistoryId}/rename`, { title });
    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to rename chat");
    }
    return res.data;
  };

  /** DELETE /chat/{chat_history_id} — permanently delete a chat */
  const deleteChatHistory = async (chatHistoryId: string) => {
    await client.delete(`/chat/${chatHistoryId}`);
  };

  // ────────────────────────────────────────────
  // Deep Thinking (Agent) APIs
  // ────────────────────────────────────────────

  /** POST /agent/query — start an agentic deep thinking task */
  const startAgentQuery = async (
    projectId: string,
    payload: {
      agent_plan: string;
      prompt_template_id: string;
      include_tag_ids?: string[];
      exclude_tag_ids?: string[];
      number_of_iterations?: number;
      report_template_id?: string;
      // Surface discriminator (see backend ChatSource). Defaults to
      // "interactive_chat" so the add-in's chat page (Deep mode) is
      // visible in the chat with documents page. Pass
      // "non_interactive_chat" from non-interactive callers.
      source?: "interactive_chat" | "non_interactive_chat";
      previous_chat_history_id?: string;
      title?: string;
      grounding?: boolean;
    }
  ) => {
    const cleanPayload: Record<string, any> = {
      project_id: projectId,
      agent_plan: payload.agent_plan,
      prompt_template_id: payload.prompt_template_id,
      number_of_iterations: payload.number_of_iterations || 5,
      // Default to the interactive surface so the add-in's chat page
      // (Lite + Deep) is the only caller. Other callers (none today)
      // would pass "non_interactive_chat" explicitly.
      source: payload.source ?? "interactive_chat",
      grounding: payload.grounding !== false,
    };
    if (payload.previous_chat_history_id) {
      cleanPayload.previous_chat_history_id = payload.previous_chat_history_id;
    }
    if (payload.title) {
      cleanPayload.title = payload.title;
    }
    if (
      payload.report_template_id &&
      payload.report_template_id !== "default" &&
      payload.report_template_id.trim() !== ""
    ) {
      cleanPayload.report_template_id = payload.report_template_id;
    }
    if (payload.include_tag_ids && payload.include_tag_ids.length > 0) {
      cleanPayload.include_tag_ids = payload.include_tag_ids;
    }
    if (payload.exclude_tag_ids && payload.exclude_tag_ids.length > 0) {
      cleanPayload.exclude_tag_ids = payload.exclude_tag_ids;
    }

    const res = await client.post("/agent/query", cleanPayload);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(res.data?.error || res.data?.message || "Failed to start agent query");
    }
    return res.data;
  };

  /** GET /agent/status/{task_id} — poll task status */
  const getAgentTaskStatus = async (taskId: string) => {
    const res = await client.get(`/agent/status/${taskId}`);
    return res.data;
  };

  /**
   * GET /agent/status/{task_id}/stream — SSE stream for agent progress.
   * Returns an AbortController to cancel the stream.
   */
  const streamAgentTaskStatus = (
    taskId: string,
    onMessage: (data: any) => void,
    onError: (error: any) => void,
    onClose: (intentional: boolean) => void
  ): AbortController => {
    const controller = new AbortController();
    let lastResponseLength = 0;
    let buffer = "";

    const fetchStream = async () => {
      try {
        await client.get(`/agent/status/${taskId}/stream`, {
          signal: controller.signal,
          responseType: "text",
          timeout: 0,
          headers: {
            Accept: "text/event-stream",
          },
          onDownloadProgress: (progressEvent: any) => {
            try {
              const xhr = progressEvent?.event?.target as XMLHttpRequest;
              const responseText = xhr?.responseText || progressEvent?.target?.responseText || "";
              if (!responseText) return;

              const newText = responseText.substring(lastResponseLength).replace(/\r/g, "");
              lastResponseLength = responseText.length;

              buffer += newText;
              const lines = buffer.split("\n");
              buffer = lines.pop() || "";

              for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed.startsWith("data: ")) {
                  try {
                    const jsonStr = trimmed.slice(6);
                    if (jsonStr) {
                      const data = JSON.parse(jsonStr);
                      onMessage(data);
                    }
                  } catch {
                    // Skip malformed or non-JSON SSE lines
                  }
                }
              }
            } catch (chunkErr) {
              Logger.warn("useBackendAPIs: stream chunk processing error:", chunkErr);
            }
          },
        });
        onClose(false);
      } catch (err: any) {
        if (err.name === "CanceledError" || err.name === "AbortError" || controller.signal.aborted) {
          onClose(true);
        } else {
          onError(err);
          onClose(false);
        }
      }
    };

    fetchStream();
    return controller;
  };


  // Return all the API functions as an object. Memoized so callers can list
  // these functions as effect dependencies: fresh identities every render made
  // those effects re-fire on every render, and once signed out each re-fire
  // 401'd into logout(), which re-rendered again. Every function above closes
  // over only `client` and `searchType`, so those are the complete deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({
    retrieveKeyStatus,
    retrieveProjects,
    retrieveSettingsGroup,
    retrieveTags,
    retrieveLLMOuput,
    retrieveLLMOuputAsync,
    retrieveReportTemplates,
    retrieveBase64,
    retrievePlaceholders,
    retrieveChatHistory,
    retrieveChatEvaluation,
    retrieveChatComparison,
    retrieveChatAsyncTask,
    streamChatAsyncTask,
    waitForChatAsyncResult,
    submitFeedback,
    retrievellmOuputWOGroup,
    retrievellmOuputWOGroupAsync,
    retrieveSourceDocument,
    uploadDocument,
    retreiveSourceDocumentMetadata,
    deleteDocument,
    createTag,
    deleteTag,
    transcribeAudio,
    synthesizeSpeech,
    speechApi,
    retrieveAgentQuery,
    streamGetRequest,
    retrieveAgentStatus,
    retrieveAgentTasks,
    retrieveAgentGrounding,
    processDocument,
    retrieveDocumentsList,
    retrieveDocumentsForImageInsertion,
    retrieveDocumentImages,
    downloadDocumentImage,
    submitLangfuseFeedback,
    logAuditAction,
    linkProjectToTemplate,
    // Chat History Management
    getProjectChatHistory,
    getChatThread,
    setChatPinned,
    renameChatHistory,
    deleteChatHistory,
    // Deep Thinking (Agent)
    startAgentQuery,
    getAgentTaskStatus,
    streamAgentTaskStatus,
    // Model & Tag Retrieval
    getModels,
    getProjectTags,
    getPromptTemplates,
  }), [client, searchType]);
};

export default useBackendAPIs;
